import {
  Alert,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import {
  CloseOutlined,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
  WarningOutlined,
} from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useState } from "react"
import { twMerge } from "tailwind-merge"

import { InventoryProductDescription, InventoryQuantityDifference } from "../../../components"
import { useTransferLookups } from "../hooks/useTransferLookups"
import type { TransferReceiveFormValues } from "../schemas/receive"
import { getLotRowBorderClassName } from "../utils/transfer"

import { Button } from "@/components/common/Button"
import { ProductStatusCell } from "@/components/table"
import type { Transfer } from "@/graphql/codegen/graphql"
import { formatNumber } from "@/utils/number"

interface TransferQuantityVarianceDialogProps {
  transfer: Transfer
  pallets: TransferReceiveFormValues["pallets"]
  onClose: () => void
  onAdjustAutomatically: () => void
  onAdjustManually: () => void
}

export const TransferQuantityVarianceDialog: FC<TransferQuantityVarianceDialogProps> = ({
  transfer,
  pallets,
  onClose,
  onAdjustAutomatically,
  onAdjustManually,
}) => {
  const [isAutoAdjustmentInfoExpanded, setIsAutoAdjustmentInfoExpanded] = useState(false)

  const { productsByReferenceNbr, transferLotsById } = useTransferLookups(transfer)

  return (
    <Dialog open onClose={onClose} maxWidth={false} data-testid='transfer.quantity-variance-dialog'>
      <div className='flex justify-end px-6 pt-4'>
        <IconButton onClick={onClose} data-testid='transfer.quantity-variance-dialog.close-button'>
          <CloseOutlined />
        </IconButton>
      </div>
      <DialogContent className='flex flex-col items-center gap-6 px-10 pb-10 pt-0'>
        <div className='flex flex-col items-center gap-4'>
          <WarningOutlined color='error' sx={{ fontSize: 48 }} />
          <Typography variant='h5' component='h2' className='text-center text-primary'>
            Quantity Variance Detected
          </Typography>
        </div>
        <div className='flex flex-col items-center gap-10'>
          <Typography className='text-center text-gray-700'>
            Received quantities do not match expected for one or more lots.
            <br />
            <b>Choose whether to perform an auto-adjustment, or manually adjust it yourself at your leisure.</b>
          </Typography>
          <Table>
            <TableHead className='bg-gray-100'>
              <TableRow>
                <TableCell>Product ID</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Reference #</TableCell>
                <TableCell>Lot #</TableCell>
                <TableCell>Batch #</TableCell>
                <TableCell className='text-right'>Qty Expected</TableCell>
                <TableCell className='text-right'>Qty Received</TableCell>
                <TableCell className='text-right'>Difference</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pallets.flatMap((pallet) => {
                const product = productsByReferenceNbr.get(pallet.referenceNbr)!
                const uomName = product.packageUsWeightUomDetails?.name ?? ""

                return pallet.lots.map((lot, lotIndex) => {
                  const isFirstLot = lotIndex === 0
                  const isLastLot = lotIndex === pallet.lots.length - 1
                  const transferLot = transferLotsById.get(lot.transferLotId)!

                  return (
                    <TableRow
                      key={lot.transferLotId}
                      className={twMerge("h-16", getLotRowBorderClassName(isLastLot))}
                      data-testid={`transfer.quantity-variance-dialog.row-${lot.transferLotId}`}
                    >
                      {isFirstLot ? (
                        <>
                          <TableCell className='py-2'>
                            <ProductStatusCell product={product} />
                          </TableCell>
                          <TableCell className='py-2'>
                            <InventoryProductDescription product={product} />
                          </TableCell>
                          <TableCell>{pallet.referenceNbr}</TableCell>
                        </>
                      ) : (
                        <TableCell colSpan={3} />
                      )}
                      <TableCell>{transferLot.rawInventory?.lotNbr}</TableCell>
                      <TableCell>{transferLot.rawInventory?.batchNbr}</TableCell>
                      <TableCell className='whitespace-nowrap text-right'>
                        {formatNumber(lot.qtyExpected)} {uomName}
                      </TableCell>
                      <TableCell className='whitespace-nowrap text-right'>
                        {formatNumber(lot.qtyReceived ?? 0)} {uomName}
                      </TableCell>
                      <TableCell className='whitespace-nowrap text-right'>
                        <InventoryQuantityDifference
                          quantity={lot.qtyReceived}
                          originalQuantity={lot.qtyExpected}
                          uomName={uomName}
                        />
                      </TableCell>
                    </TableRow>
                  )
                })
              })}
            </TableBody>
          </Table>
          <div className='w-full rounded border border-gray-400'>
            <div className='flex items-center justify-between p-4'>
              <Typography component='h3' className='text-gray-800'>
                How Auto-Adjustment Works
              </Typography>
              <IconButton
                onClick={() => setIsAutoAdjustmentInfoExpanded((prev) => !prev)}
                aria-expanded={isAutoAdjustmentInfoExpanded}
                aria-label='show more'
                data-testid='transfer.quantity-variance-dialog.auto-adjustment-info-toggle-button'
              >
                {isAutoAdjustmentInfoExpanded ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
              </IconButton>
            </div>
            <Collapse in={isAutoAdjustmentInfoExpanded}>
              <div className='flex flex-col gap-4 px-4 pb-4 pt-2 text-gray-700'>
                <div>
                  <Typography variant='subtitle2'>1. Inventory Adjustment Per Lot</Typography>
                  <Typography variant='body2'>
                    The system creates an adjustment for each lot with a variance. Each lot is processed independently.
                  </Typography>
                </div>
                <div>
                  <Typography variant='subtitle2'>
                    2. Work Order: Move All Pallets in Transit to Inbound Hold
                  </Typography>
                  <div className='flex items-center gap-2'>
                    <Typography variant='body2'>
                      Only triggered if <b>all</b> lot adjustments succeed. Transfer is then marked as
                    </Typography>
                    <Chip className='h-6 rounded bg-tertiary-pink-300 text-xs' label='Received' size='small' />
                  </div>
                </div>
                <Alert variant='filled' severity='warning'>
                  <div>
                    If even <b>one lot's adjustment fails</b>, no pallets are moved and the transfer stays open.
                    <br /> You can retry lots or mark received manually.
                  </div>
                </Alert>
              </div>
            </Collapse>
          </div>
          <div className='flex w-full flex-col gap-1'>
            <div className='flex items-center justify-between'>
              <Button
                appearance='outlined'
                onClick={onClose}
                data-testid='transfer.quantity-variance-dialog.go-back-button'
              >
                Go Back
              </Button>
              <div className='flex gap-x-6'>
                <Button
                  appearance='outlined'
                  onClick={onAdjustAutomatically}
                  data-testid='transfer.quantity-variance-dialog.auto-adjust-button'
                >
                  Auto-Adjust*
                </Button>
                <Button
                  appearance='contained'
                  onClick={onAdjustManually}
                  data-testid='transfer.quantity-variance-dialog.adjust-manually-button'
                >
                  Adjust Manually
                </Button>
              </div>
            </div>
            <Typography variant='body2' className='text-right text-gray-700'>
              *Auto-Adjustments are faster but have a slight probability of failure
            </Typography>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
